/**
 * Live task tracker.
 *
 * A floating checklist of every module in the tour, in the order the tour visits
 * them — not grouped under a theme the user has to expand first. A module's
 * submodules appear nested directly under it the moment the tour actually
 * reaches that module (see `TourEngine`'s auto-expand on highlight), so the list
 * mirrors the walk itself rather than requiring a separate action to reveal
 * what is inside one.
 *
 * Also the tour's primary navigation surface: every row jumps there directly.
 *
 * Portaled to `document.body` so its stacking context is its own: it has to sit
 * ABOVE driver.js's overlay (`z-index: 1000000000`) or the dim layer would grey
 * it out and swallow its clicks — the one thing a tracker must never do is stop
 * responding while the tour it tracks is running.
 */

import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import type { StageProgress, StepProgress } from '../tours';

interface ModuleRow {
  module: StepProgress;
  children: StepProgress[];
}

/**
 * Flatten the stage-grouped progress view into one ordered list of modules,
 * each carrying whatever submodules the run has reached so far.
 *
 * `tours.ts` still groups run positions into stages internally — that is what
 * `computeStageProgress` uses to work out each step's state — but this tracker
 * no longer shows that grouping. Every module gets its own row, in the order
 * the tour actually visits them, and a stage boundary is invisible here.
 */
function toModuleRows(stages: StageProgress[]): ModuleRow[] {
  const rows: ModuleRow[] = [];
  for (const stage of stages) {
    for (const step of stage.steps) {
      if (step.depth === 0) rows.push({ module: step, children: [] });
      else rows[rows.length - 1]?.children.push(step);
    }
  }
  return rows;
}

export function TourTracker() {
  const tour = useTourGuide();
  const listRef = React.useRef<HTMLOListElement>(null);

  // Hooks run every render regardless of the early return below, so this
  // reads `undefined` rather than being skipped while idle.
  const activeStepId = tour?.run.running ? tour.run.stepId : null;

  /**
   * Keep the active row on screen as the tour advances.
   *
   * 🔴 WITHOUT THIS the list never followed the tour on its own — it is a
   * flat checklist of every module (up to 17, more once a task-bearing
   * submodule is reached), so once the active step scrolled past the visible
   * ~460px panel, Next kept moving the real highlight around the page while
   * this list just sat still until the user scrolled it by hand.
   *
   * Keyed on the step id, not the whole `run` object, so an unrelated field
   * changing (e.g. `total` growing when a module's submodules are spliced in)
   * cannot fire a scroll that has nothing to do with where the user is.
   */
  React.useEffect(() => {
    if (!activeStepId) return;
    const active = listRef.current?.querySelector('[aria-current="step"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeStepId]);

  // No provider, or nothing running — nothing to track.
  if (!tour || !tour.run.running) return null;

  const modules = toModuleRows(tour.stageProgress.stages);
  if (modules.length === 0) return null;

  // "Module N of M" reads as overall progress even while standing on a
  // submodule — its parent module is by then `complete` (the run has moved
  // past its own position), so it is already counted.
  const completedModules = modules.filter((m) => m.module.state === 'complete').length;
  const activeModuleIndex = modules.findIndex((m) => m.module.state === 'active');
  const currentModuleNumber = activeModuleIndex >= 0 ? activeModuleIndex + 1 : completedModules;

  const { index, total } = tour.run;

  return createPortal(
    <aside
      className="tour-guide-tracker"
      data-testid="tour-tracker"
      // Announced politely: it is progress information, not something the user
      // must be interrupted for.
      role="status"
      aria-live="polite"
      aria-label={`Tour progress: module ${currentModuleNumber || 1} of ${modules.length}`}
      // driver.js closes on overlay click when `allowClose` is set; the tracker
      // renders above that overlay, so a click here must not travel onward and
      // dismiss the tour the user is navigating.
      onClick={(e) => e.stopPropagation()}
    >
      <header className="tour-guide-tracker__head">
        <span className="tour-guide-tracker__title">Product tour</span>
        <span className="tour-guide-tracker__count" data-testid="tour-tracker-count">
          Module {currentModuleNumber || 1} / {modules.length}
        </span>
        <button
          type="button"
          className="tour-guide-tracker__close"
          onClick={() => tour.stop()}
          aria-label="End tour"
          title="End tour"
          data-testid="tour-tracker-exit"
        >
          ×
        </button>
      </header>

      <ol className="tour-guide-tracker__list" ref={listRef}>
        {modules.map(({ module, children }, i) => (
          <li
            key={module.id}
            className="tour-guide-tracker__item"
            data-state={module.state}
            data-testid={`tour-module-${module.id}`}
          >
            {/*
              The task chip is a SIBLING of the jump button, never nested inside
              it: a button within a button is invalid markup and the inner click
              would also fire the outer one, so one tap would both start the flow
              and jump the tour.
            */}
            <div className="tour-guide-tracker__steprowwrap">
              <button
                type="button"
                className="tour-guide-tracker__row"
                onClick={() => tour.goToStep(module.id)}
                aria-current={module.state === 'active' ? 'step' : undefined}
              >
                <span className="tour-guide-tracker__marker" aria-hidden="true">
                  {module.state === 'complete' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <span className="tour-guide-tracker__dot" />
                  )}
                </span>
                <span className="tour-guide-tracker__label">{module.label}</span>
              </button>

              {tour.taskFor(module.id) && (
                <button
                  type="button"
                  className="tour-guide-tracker__steptask"
                  onClick={() => tour.startTask(module.id)}
                  title={`Build one yourself: ${module.label}`}
                  aria-label={`Start the guided walkthrough for ${module.label}`}
                  data-testid={`tour-step-task-${module.id}`}
                >
                  Try
                </button>
              )}
            </div>

            {/*
              Submodules appear only once the engine has actually spliced them
              into the run — see `TourEngine`'s auto-expand on highlight. A
              module further ahead that has not been reached yet simply has none
              to show here yet, exactly mirroring what the tour will do next.
            */}
            {children.length > 0 && (
              <ol className="tour-guide-tracker__steplist">
                {children.map((step) => (
                  <li
                    key={step.id}
                    className="tour-guide-tracker__step"
                    data-state={step.state}
                    data-depth={step.depth}
                    data-testid={`tour-step-${step.id}`}
                  >
                    <div className="tour-guide-tracker__steprowwrap">
                      <button
                        type="button"
                        className="tour-guide-tracker__steprow"
                        onClick={() => tour.goToStep(step.id)}
                        aria-current={step.state === 'active' ? 'step' : undefined}
                      >
                        <span className="tour-guide-tracker__stepdot" aria-hidden="true" />
                        <span className="tour-guide-tracker__steplabel">{step.label}</span>
                      </button>

                      {tour.taskFor(step.id) && (
                        <button
                          type="button"
                          className="tour-guide-tracker__steptask"
                          onClick={() => tour.startTask(step.id)}
                          title={`Build one yourself: ${step.label}`}
                          aria-label={`Start the guided walkthrough for ${step.label}`}
                          data-testid={`tour-step-task-${step.id}`}
                        >
                          Try
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {i < modules.length - 1 && (
              <span className="tour-guide-tracker__connector" aria-hidden="true" />
            )}
          </li>
        ))}
      </ol>

      <footer className="tour-guide-tracker__foot">
        <span className="tour-guide-tracker__pos" data-testid="tour-tracker-pos">
          Step {index + 1} of {total}
        </span>
      </footer>
    </aside>,
    document.body,
  );
}
